import { Button, ChipsAutocomplete, ChipsAutocompleteOptionType, Icon, TextInput, theme } from '@ketch-com/deck'
import { Box } from '@mui/material'
import { usePurposesV2 } from 'api/purposesV2/queries/usePurposesV2'
import { FieldArray, useField } from 'formik'
import { AppFormFieldDTO } from 'interfaces/apps'
import { useMemo } from 'react'

type OneTrustPurposeGroupMappingProps = {
  field: AppFormFieldDTO
  index: number
}

export const OneTrustPurposeGroupMapping: React.FC<OneTrustPurposeGroupMappingProps> = ({ index }) => {
  const [formikField, meta] = useField<
    {
      appPurpose: string
      ketchPurposeCodes: string[]
    }[]
  >(`fields.${index}.migratorPurposeMapping`)
  const { data: purposes } = usePurposesV2({})

  const isEditMode = !window.location.pathname.includes('new')

  // Only show purposes that are not already mapped to any OneTrust consent group
  const ketchPurposeOptions: ChipsAutocompleteOptionType[] = useMemo(() => {
    const ketchPurposes = formikField.value.map(mapping => mapping.ketchPurposeCodes).flat()
    return purposes?.reduce((acc, purpose) => {
      if (!ketchPurposes.includes(purpose.code)) {
        acc.push({
          label: purpose.name,
          value: purpose.code,
        })
      }
      return acc
    }, [] as ChipsAutocompleteOptionType[])
  }, [purposes, formikField.value])

  return (
    <FieldArray name={`fields.${index}.migratorPurposeMapping`}>
      {arrayHelpers => (
        <>
          <Box display="flex" flexDirection="column" gap={3}>
            {formikField.value.map((mapping, index) => {
              const rowError = meta.error?.[index] as {
                appPurpose?: string
                ketchPurposeCodes?: string
              }

              const oneTrustConsentGroupError = rowError?.appPurpose
              const ketchPurposeCodesError = rowError?.ketchPurposeCodes

              return (
                <Box
                  key={index}
                  display="flex"
                  alignItems="flex-start"
                  gap={3}
                  sx={{ borderBottom: `1px solid ${theme.palette.Common.Divider}` }}
                >
                  <TextInput
                    label="OneTrust Consent Group"
                    placeholder="Example: C0001"
                    inputWidth={isEditMode ? '485px' : '375px'}
                    value={mapping.appPurpose}
                    onChange={e => {
                      arrayHelpers.replace(index, {
                        appPurpose: e.target.value,
                        ketchPurposeCodes: mapping.ketchPurposeCodes,
                      })
                    }}
                    error={!!oneTrustConsentGroupError}
                    errorMessage={oneTrustConsentGroupError}
                  />
                  <Icon name="OArrowRight" iconColor={theme.palette.Black.o48} width={28} height={28} sx={{ mt: 4 }} />
                  <Box maxWidth="454px">
                    <ChipsAutocomplete
                      label="Ketch Purposes"
                      placeholder="Search for a purpose"
                      options={ketchPurposeOptions}
                      error={!!ketchPurposeCodesError}
                      errorMessage={ketchPurposeCodesError}
                      chipSize="medium"
                      size="medium"
                      width="454px"
                      isOptionEqualToValue={(option, value) => option?.value === value?.value}
                      getOptionLabel={option => option?.label || option.value}
                      value={mapping.ketchPurposeCodes.map(code => {
                        const ketchPurpose = purposes.find(purpose => purpose.code === code)
                        return {
                          value: code,
                          label: ketchPurpose?.name || code,
                        }
                      })}
                      // Add new chip to list
                      onChange={(_, newValue) => {
                        arrayHelpers.replace(index, {
                          appPurpose: mapping.appPurpose,
                          ketchPurposeCodes: newValue.map(option => option.value),
                        })
                      }}
                      // Remove chip from list on X click
                      handleChipClick={v => {
                        const result = mapping.ketchPurposeCodes.filter(code => code !== v.value)
                        arrayHelpers.replace(index, {
                          appPurpose: mapping.appPurpose,
                          ketchPurposeCodes: result,
                        })
                      }}
                      multiple
                      inRowChips
                    />
                  </Box>

                  {formikField.value.length > 1 && (
                    <Button
                      variant="iconCustomRounded"
                      color="tertiary"
                      onClick={() => arrayHelpers.remove(index)}
                      sx={{ mt: 4 }}
                    >
                      <Icon name="OBin" />
                    </Button>
                  )}
                </Box>
              )
            })}
          </Box>
          <Button
            variant="outlined"
            startIcon={<Icon name="OPlus" />}
            onClick={() =>
              arrayHelpers.push({
                appPurpose: '',
                ketchPurposeCodes: [],
              })
            }
            sx={{ mt: 3 }}
          >
            Add Purpose Mapping
          </Button>
        </>
      )}
    </FieldArray>
  )
}
